import { BalanceData } from "../data/balance";
import { NO_VIP_BENEFITS, VipBenefits, VipTable } from "../data/vip";
import { Fix64 } from "../maths/fix64";
import { GearBonus } from "./gear";
import { utcDay } from "./orders";

/**
 * VIP v2 rules (docs/SHOP.md §8; balance/src/vip.json; owner decisions 2026-09-26). Pure and deterministic; the wall
 * clock is passed in as Unix seconds.
 * - XP: every real-money purchase (xpPerDollar × whole dollars, rounded up) and the daily claim streak (`streakXp`). Levels
 *   never drop.
 * - Streak (owner 2026-10-24): the daily VIP XP is claimed on the VIP page, once a UTC day (00:00 UTC, like every
 *   daily). Claiming on consecutive days grows it along the anchors; a UTC day without a claim starts it over at day 1.
 *   A clock set back pays nothing.
 * - Benefits: the level's row. Combat reaches a level ONLY through the profile's per-archetype GearBonus
 *   (`withVipCombat`): fixed for the run and hashed like gear, and unchanged at VIP 0 (so replays and goldens at VIP 0
 *   do not move). Build speed divides upgrade timers; resource production multiplies Tribute and battle-chest
 *   resources after their roll (VIP-CORE wires both).
 * It replaced pass 1's meta/vip.ts (offers.json vip, QoL only); `VipRecord.fromJson` still reads pass 1's save section.
 */

const CENTS_PER_DOLLAR = 100;
const PERCENT = 100;

/** VIP XP a purchase at this price gives: xpPerDollar × whole dollars rounded up ($4.99 → 500). */
export function vipXpForPrice(table: VipTable, priceCents: number): number {
  return priceCents > 0 ? table.xpPerDollar * Math.ceil(priceCents / CENTS_PER_DOLLAR) : 0;
}

/** The VIP level of an XP total: the highest level whose threshold is reached (0 without VIP data). */
export function vipTierAt(table: VipTable, xp: number): number {
  let level = 0;
  table.levels.forEach((l, i) => { if (xp >= l.xp) level = i; });
  return level;
}

/** The benefits of an XP total's level (all zero at VIP 0 or without VIP data). */
export function vipBenefits(table: VipTable, xp: number): VipBenefits {
  return table.levels[vipTierAt(table, xp)] ?? NO_VIP_BENEFITS;
}

export interface VipTierProgress {
  readonly level: number;
  readonly xp: number;
  /** The next level's threshold, or null at the top ("Maxed"). */
  readonly nextAt: number | null;
  /** 0..1 of the way from this level's threshold to the next (1 at the top). */
  readonly fraction: number;
}

export function vipTierProgress(table: VipTable, xp: number): VipTierProgress {
  const level = vipTierAt(table, xp), here = table.levels[level]?.xp ?? 0, next = table.levels[level + 1]?.xp ?? null;
  return { level, xp, nextAt: next, fraction: next === null ? 1 : Math.min(1, (xp - here) / (next - here)) };
}

/** XP the claim of streak day `day` (1 = the first day) pays: linear between the anchors, flat outside them. */
export function streakXp(table: VipTable, day: number): number {
  const a = table.streak.anchors;
  if (a.length === 0 || day <= a[0][0]) return a[0]?.[1] ?? 0;
  for (let i = 1; i < a.length; i++) {
    const [d0, x0] = a[i - 1], [d1, x1] = a[i];
    if (day <= d1) return Math.floor(x0 + ((x1 - x0) * (day - d0)) / (d1 - d0));
  }
  return a[a.length - 1][1];
}

/**
 * The streak a claim on UTC day `today` counts, from `streak` last counted on `lastDay` (−1: never): the day after
 * continues it, the same day keeps it, anything else (a missed day, or a clock set back past it) starts over at day 1.
 */
export function streakAfter(streak: number, lastDay: number, today: number): number {
  if (lastDay < 0) return 1;
  if (today === lastDay) return Math.max(1, streak);
  return today === lastDay + 1 ? streak + 1 : 1;
}

/** The `vip` save section, version 3 (owner 2026-10-24: the daily VIP XP is claimed, not paid on the hub visit). */
export interface VipRecordJson { version: 3; xp: number; streak: number; streakDay: number; claimDay: number; bundleDay: number }
/** Any saved `vip` section as read: v3, v2 ({xp, streak, loginDay, bundleDay}: the streak paid by the day's first hub visit) or pass 1 ({points, loginDay, chestDay}). */
interface VipAnyJson {
  version?: unknown; xp?: unknown; streak?: unknown; streakDay?: unknown; claimDay?: unknown; bundleDay?: unknown;
  loginDay?: unknown; points?: unknown; chestDay?: unknown;
}

const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0;
const isDay = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= -1;

/** VIP state: XP, the claim streak, the UTC days it was last counted and claimed, and the day of the last daily bundle. */
export class VipRecord {
  xp = 0;
  /** Streak days counted at the last claim (0: none yet). */
  streak = 0;
  /** UTC day the streak was last counted (−1 never). Equal to `claimDay` except right after a migration. */
  streakDay = -1;
  /** UTC day the daily VIP XP was last claimed (−1 never). */
  claimDay = -1;
  /** UTC day the daily bundle was last claimed (−1 never). */
  bundleDay = -1;

  /** Adds XP (a purchase; the caller keeps it idempotent by transaction id). */
  addXp(n: number): void { if (Number.isInteger(n) && n > 0) this.xp += n; }

  level(table: VipTable): number { return vipTierAt(table, this.xp); }
  benefits(table: VipTable): VipBenefits { return vipBenefits(table, this.xp); }

  /** Whether today's VIP XP waits: not claimed this UTC day, and the clock is not behind the streak. */
  claimReady(now: number): boolean {
    const today = utcDay(now);
    return today > this.claimDay && today >= this.streakDay;
  }

  /** The streak day today's claim counts (claimed or not): the day after the last claim continues it, a missed day restarts at 1. */
  todayStreak(now: number): number {
    const today = utcDay(now);
    return today === this.claimDay ? this.streak : streakAfter(this.streak, this.streakDay, today);
  }

  /** What today's claim pays (or paid): the streak day it counts. */
  todayXp(table: VipTable, now: number): number { return streakXp(table, this.todayStreak(now)); }

  /** What tomorrow's claim pays if today's is claimed and no day is missed. */
  tomorrowXp(table: VipTable, now: number): number { return streakXp(table, this.todayStreak(now) + 1); }

  /** Claims today's VIP XP: continues the streak (a missed day starts it over) and adds its XP. Returns the XP added (0 when not ready). */
  claimDaily(table: VipTable, now: number): number {
    if (!this.claimReady(now)) return 0;
    const today = utcDay(now);
    this.streak = this.todayStreak(now);
    this.streakDay = this.claimDay = today;
    const xp = streakXp(table, this.streak);
    this.addXp(xp);
    return xp;
  }

  /** Whether today's free bundle is there: the level has one and it was not claimed this UTC day. */
  bundleReady(table: VipTable, now: number): boolean { return this.benefits(table).dailyBundle.length > 0 && utcDay(now) > this.bundleDay; }

  /** Marks today's bundle claimed; the caller pays `benefits(table).dailyBundle` (meta/deals.ts grantItems). */
  takeBundle(table: VipTable, now: number): boolean {
    if (!this.bundleReady(table, now)) return false;
    this.bundleDay = utcDay(now);
    return true;
  }

  toJson(): VipRecordJson {
    return { version: 3, xp: this.xp, streak: this.streak, streakDay: this.streakDay, claimDay: this.claimDay, bundleDay: this.bundleDay };
  }

  /**
   * Lenient restore, field by field. Migrates version 2 ({xp, streak, loginDay, bundleDay}: the streak carries over,
   * counted on its login day, and today's claim waits even when that login paid today) and pass 1's section ({points,
   * loginDay, chestDay}): points become XP (levels never drop), a pass-1 login starts the streak at 1, and a VIP chest
   * claimed today counts as today's bundle.
   */
  static fromJson(j: unknown): VipRecord {
    const v = new VipRecord();
    if (typeof j !== "object" || j === null) return v;
    const s = j as VipAnyJson;
    if (s.version === 3 || s.version === 2) {
      if (isCount(s.xp)) v.xp = s.xp;
      if (isCount(s.streak)) v.streak = s.streak;
      if (isDay(s.bundleDay)) v.bundleDay = s.bundleDay;
      if (s.version === 2) { if (isDay(s.loginDay)) v.streakDay = s.loginDay; return v; }
      if (isDay(s.streakDay)) v.streakDay = s.streakDay;
      if (isDay(s.claimDay)) v.claimDay = s.claimDay;
      return v;
    }
    if (isCount(s.points)) v.xp = s.points;
    if (isDay(s.loginDay)) { v.streakDay = s.loginDay; v.streak = s.loginDay >= 0 ? 1 : 0; }
    if (isDay(s.chestDay)) v.bundleDay = s.chestDay;
    return v;
  }
}

// ------------------------------------------------------------------ combat: only through GearBonus

/** A level's combat bonuses as fractions (16 % → 0.16). */
export interface VipCombat { readonly towerDamage: Fix64; readonly fortressHp: Fix64; readonly troops: Fix64 }

export function vipCombat(b: VipBenefits): VipCombat {
  const f = (p: number) => (p === 0 ? Fix64.Zero : Fix64.fromFraction(p, PERCENT));
  return { towerDamage: f(b.towerDamagePercent), fortressHp: f(b.fortressHpPercent), troops: f(b.troopPercent) };
}

/**
 * The per-archetype GearBonus a level runs with: the equipped gear's, plus VIP's tower damage on every archetype (the
 * fortress's own tower included), VIP's fortress health on the base and VIP's troop strength on the base (`troops`:
 * the troops' stat multiplier × (1 + troops), sim/troops.ts; hashed only when nonzero). VIP 0 returns the gear bonuses
 * untouched, so a VIP-0 run hashes exactly as before.
 */
export function withVipCombat(balance: BalanceData, bonuses: readonly GearBonus[], b: VipBenefits): GearBonus[] {
  const c = vipCombat(b);
  if (c.towerDamage.isZero() && c.fortressHp.isZero() && c.troops.isZero()) return [...bonuses];
  const base = balance.towers.findIndex((t) => t.isBase);
  return bonuses.map((g, i) => {
    const out: GearBonus = { ...g, damage: g.damage.add(c.towerDamage), hp: i === base ? g.hp.add(c.fortressHp) : g.hp };
    return i === base && !c.troops.isZero() ? { ...out, troops: (g.troops ?? Fix64.Zero).add(c.troops) } : out;
  });
}

/**
 * The tower levels a tower-damage bonus equals (docs/SHOP.md §8.3): a tower level multiplies damage by the level curve's
 * growth, so +d % is ln(1 + d) / ln(growth) levels (16 % ≈ 5.0 at 1.03), to a tenth. The readiness strip
 * adds it to the towers meter so Ready keeps meaning a ~70 % win for this player. Presentation arithmetic (floats): never
 * a sim input.
 */
export function vipTowerLevels(balance: BalanceData, towerDamagePercent: number): number {
  if (towerDamagePercent <= 0 || balance.tables.maxLevel < 2) return 0;
  const growth = balance.tables.levelMultAt(2).toNumber() / balance.tables.levelMultAt(1).toNumber();
  if (!(growth > 1)) return 0;
  // Rounded, not floored: the table's growth is 1.03 to Fix64 precision, so +3 % is 0.99999… levels and means 1.
  const TENTHS = 10;
  return Math.round((Math.log(1 + towerDamagePercent / PERCENT) / Math.log(growth)) * TENTHS) / TENTHS;
}

/** An upgrade timer under VIP build speed: seconds / (1 + b), rounded up; unchanged at 0. */
export function vipBuildSeconds(seconds: number, buildSpeedPercent: number): number {
  return buildSpeedPercent > 0 ? Math.ceil((seconds * PERCENT) / (PERCENT + buildSpeedPercent)) : seconds;
}

/** A produced resource amount under VIP resource production: × (1 + r), rounded down; unchanged at 0. */
export function vipResources(amount: number, resourcePercent: number): number {
  return resourcePercent > 0 ? Math.floor((amount * (PERCENT + resourcePercent)) / PERCENT) : amount;
}
